import React, { useContext, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Box, 
  Stack, 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery, 
  useTheme,
  alpha,
} from '@mui/material';
import { Brightness4, Brightness7, KeyboardArrowDown, Menu as MenuIcon } from '@mui/icons-material';
import { ColorModeContext } from '../App';
import LoginButton from './LoginButton';
import UserMenu, { UserAvatar } from './UserMenu';
import { useIsAdmin } from './useIsAdmin';
import { navMenuItemSx, useNavMenu } from './navMenuStyles';
import { useAuth0 } from '@auth0/auth0-react';

const Navbar: React.FC = () => {
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const { isAuthenticated, user, logout } = useAuth0();
  const isAdmin = useIsAdmin();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const dataMenu = useNavMenu('left');
  const location = useLocation();

  const handleMobileDrawerOpen = () => {
    setMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  // Pages grouped under the "Data" menu (all require login).
  const dataItems = [
    { label: 'Loop Data', path: '/loop-data' },
    { label: 'Track Rankings', path: '/track-rankings' },
    { label: 'Track Similarity', path: '/track-similarity' },
    { label: 'Research', path: '/research' },
  ];
  // Top-level links after the Data menu.
  const allNavigationItems = [
    { label: 'Simulator', path: '/simulator', requiresAuth: true },
  ];

  // Only show auth-required items if the user is authenticated.
  const navigationItems = allNavigationItems.filter((item) => !item.requiresAuth || isAuthenticated);
  const showData = isAuthenticated;
  const onDataPage = dataItems.some((d) => location.pathname === d.path || location.pathname.startsWith(`${d.path}/`));
  const drawerItemSx = {
    borderRadius: 1,
    mb: 0.5,
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(98, 191, 123, 0.1)' : 'rgba(46, 125, 50, 0.1)',
    },
  };
  const drawerTextSx = {
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      color: mode === 'dark' ? '#fff' : '#333',
    },
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
          <Box
            component={RouterLink}
            to="/"
            aria-label="Left Turn Betting home"
            sx={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}
          >
            <Box
              component="img"
              // Transparent logo in the theme's green (a darker green on light backgrounds).
              src={`${process.env.PUBLIC_URL}/images/logo-nav-${mode === 'dark' ? 'dark' : 'light'}.png`}
              alt="Left Turn Betting"
              sx={{ height: { xs: 30, md: 38 }, width: 'auto', display: 'block' }}
            />
          </Box>
        </Stack>
        
        {isMobile ? (
          <>
            <IconButton onClick={toggleColorMode} color="inherit" sx={{ mr: 1 }}>
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileDrawerOpen}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileDrawerOpen}
              onClose={handleMobileDrawerClose}
              sx={{
                '& .MuiDrawer-paper': {
                  width: 280,
                  background: mode === 'dark' 
                    ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                    : 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
                  borderLeft: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
                },
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    fontFamily: "'Orbitron', sans-serif",
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    background: mode === 'dark' 
                      ? 'linear-gradient(45deg, #62BF7B 30%, #90EFA5 90%)'
                      : 'linear-gradient(45deg, #2E7D32 30%, #62BF7B 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textAlign: 'center'
                  }}
                >
                  Navigation
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {showData && (
                    <>
                      <ListSubheader disableSticky sx={{ bgcolor: 'transparent', lineHeight: '32px', px: 1 }}>
                        Data
                      </ListSubheader>
                      {dataItems.map((item) => (
                        <ListItem key={item.label} disablePadding>
                          <ListItemButton
                            component={RouterLink}
                            to={item.path}
                            onClick={handleMobileDrawerClose}
                            sx={{ ...drawerItemSx, pl: 3 }}
                          >
                            <ListItemText primary={item.label} sx={drawerTextSx} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}
                  {navigationItems.map((item) => (
                    <ListItem key={item.label} disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileDrawerClose}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(98, 191, 123, 0.1)' 
                              : 'rgba(46, 125, 50, 0.1)',
                          },
                        }}
                      >
                        <ListItemText 
                          primary={item.label}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontWeight: 500,
                              color: mode === 'dark' ? '#fff' : '#333',
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {navigationItems.length > 0 && <Divider sx={{ my: 2 }} />}
                  {isAuthenticated ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, mb: 1 }}>
                        <UserAvatar size={36} />
                        <Box sx={{ minWidth: 0 }}>
                          {user?.name && user.name !== user.email && (
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {user.name}
                            </Typography>
                          )}
                          {user?.email && (
                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                              {user.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <ListItem disablePadding>
                        <ListItemButton component={RouterLink} to="/profile" onClick={handleMobileDrawerClose} sx={drawerItemSx}>
                          <ListItemText primary="Profile" sx={drawerTextSx} />
                        </ListItemButton>
                      </ListItem>
                      {isAdmin && (
                        <ListItem disablePadding>
                          <ListItemButton component={RouterLink} to="/admin" onClick={handleMobileDrawerClose} sx={drawerItemSx}>
                            <ListItemText primary="Admin" sx={drawerTextSx} />
                          </ListItemButton>
                        </ListItem>
                      )}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => {
                            handleMobileDrawerClose();
                            logout({ logoutParams: { returnTo: window.location.origin } });
                          }}
                          sx={drawerItemSx}
                        >
                          <ListItemText primary="Log out" sx={drawerTextSx} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  ) : (
                    <ListItem disablePadding>
                      <ListItemButton onClick={handleMobileDrawerClose} sx={drawerItemSx}>
                        <ListItemText primary={<LoginButton />} sx={drawerTextSx} />
                      </ListItemButton>
                    </ListItem>
                  )}
                </List>
              </Box>
            </Drawer>
          </>
        ) : (
          <>
            {showData && (
              <>
                <Button
                  color="inherit"
                  onClick={dataMenu.open}
                  endIcon={
                    <KeyboardArrowDown
                      sx={{ transition: 'transform 150ms', transform: dataMenu.isOpen ? 'rotate(180deg)' : 'none' }}
                    />
                  }
                  aria-haspopup="menu"
                  aria-expanded={dataMenu.isOpen}
                  sx={{
                    mr: 1,
                    ...((onDataPage || dataMenu.isOpen) && {
                      color: 'primary.main',
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    }),
                  }}
                >
                  Data
                </Button>
                <Menu {...dataMenu.menuProps}>
                  {dataItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      component={RouterLink}
                      to={item.path}
                      selected={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                      onClick={dataMenu.close}
                      sx={navMenuItemSx}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {navigationItems.map((item) => (
              <Button 
                key={item.label}
                color="inherit" 
                component={RouterLink} 
                to={item.path}
                sx={{ mr: 1 }}
              >
                {item.label}
              </Button>
            ))}
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={toggleColorMode} color="inherit">
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
              {isAuthenticated ? <UserMenu /> : <LoginButton />}
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 